import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '@/lib/constants/legal-urls';
import { SatisfiedRequirementIcon } from '@/modules/authorization/account-activation/components/molecules/satisfied-requirement-icon';
import { UnsatisfiedRequirementIcon } from '@/modules/authorization/account-activation/components/molecules/unsatisfied-requirement-icon';
import { AccountActivationFormValues } from '@/modules/authorization/account-activation/types';
import { accountActivationFormSchema } from '@/modules/authorization/account-activation/validation';
import { usePasswordRequirements } from '@/modules/authorization/account-activation/validation/use-password-requirements';
import { UnauthenticatedRoutes } from '@/router/routes';

interface CreatePasswordProps {
  email: string;
  header: string;
  subtitle: string;
  onSubmit: (values: {
    email: string;
    password: string;
    confirmPassword: string;
  }) => void | Promise<void>;
  isLoading?: boolean;
  passwordLabel: string;
  passwordPlaceholder: string;
  ctaLabel: string;
}

export const CreatePassword: React.FC<CreatePasswordProps> = ({
  email,
  header,
  subtitle,
  onSubmit,
  isLoading = false,
  passwordLabel,
  passwordPlaceholder,
  ctaLabel,
}) => {
  const { t } = useTranslation(['unauthenticated.createPassword', 'unauthenticated.common']);

  const defaultValues: AccountActivationFormValues = {
    email: email,
    password: '',
    confirmPassword: '',
  };

  const form = useForm<AccountActivationFormValues>({
    resolver: zodResolver(accountActivationFormSchema),
    defaultValues,
  });

  const password = form.watch('password');
  const passwordRequirements = usePasswordRequirements(password, t);

  const handleSubmit: SubmitHandler<AccountActivationFormValues> = async values => {
    await onSubmit(values);
  };

  return (
    <div className="w-full max-w-sm">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <h1 className="text-2xl font-semibold text-foreground">{header}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} disabled autoComplete="username" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="mt-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{passwordLabel}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder={passwordPlaceholder}
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="mt-4">
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('confirmPasswordLabel')}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder={t('confirmPasswordPlaceholder')}
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="mt-4 w-full rounded-md border border-input bg-background p-3 text-foreground">
            <span className="text-sm font-medium">{t('passwordRequirementsTitle')}</span>
            <ul className="mt-3 space-y-2">
              {passwordRequirements.map((req, idx) => (
                <li key={idx} className="flex items-center text-sm">
                  {req.satisfied ? <SatisfiedRequirementIcon /> : <UnsatisfiedRequirementIcon />}
                  <span className="ms-1 text-xs">{req.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <ButtonLoadable className="mt-4 w-full" type="submit" isLoading={isLoading}>
            {ctaLabel}
          </ButtonLoadable>
          <div className="mt-6 flex items-center justify-center gap-1">
            <span className="text-sm text-muted-foreground">{t('haveAccount')}</span>
            <Link to={UnauthenticatedRoutes.Login} className="text-sm text-foreground underline">
              {t('signIn')}
            </Link>
          </div>
          <div className="mt-6 px-8 text-center text-sm text-muted-foreground">
            <Trans
              i18nKey="agreement"
              t={t}
              values={{ cta: ctaLabel.toLowerCase() }}
              components={{
                terms: (
                  <a
                    href={TERMS_OF_SERVICE_URL}
                    className="whitespace-nowrap underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                privacy: (
                  <a
                    href={PRIVACY_POLICY_URL}
                    className="whitespace-nowrap underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </div>
        </form>
      </Form>
    </div>
  );
};
